<template>
  <van-pull-refresh
    v-model="refreshing"
    class="page-pull-refresh"
    @refresh="onRefresh"
  >
    <div class="user-management">
    <!-- 当前用户信息卡片 -->
    <van-card class="user-info-card">
      <template #title>
        <div class="user-info-header">
          <div class="title-container">
            <van-icon name="manager-o" size="24" color="#667eea" />
            <span class="user-info-title">当前用户信息</span>
          </div>
          <van-button
            type="primary"
            size="small"
            @click="showChangePassword = true"
            class="change-password-btn"
          >
            修改密码
          </van-button>
        </div>
      </template>
      <template #desc>
        <div class="user-info-content">
          <div class="info-item">
            <span class="label">姓名：</span>
            <span class="value">{{ currentUser.name }}</span>
          </div>
          <div class="info-item">
            <span class="label">用户名：</span>
            <span class="value">{{ currentUser.userName }}</span>
          </div>
          <div class="info-item">
            <span class="label">角色：</span>
            <span class="value">{{ currentUserRoleDesc }}</span>
          </div>
          <div class="info-item">
            <span class="label">联系电话：</span>
            <span class="value">{{ currentUser.phone }}</span>
          </div>
        </div>
      </template>
    </van-card>

    <!-- 用户管理操作 -->
    <div class="management-section">
      <div class="section-header">
        <h3>用户管理</h3>
        <van-button
          type="primary"
          size="small"
          icon="plus"
          @click="showCreateUser = true"
        >
          创建用户
        </van-button>
      </div>

      <!-- 查询条件 -->
      <div class="search-bar">
        <van-field
          v-model="searchForm.userName"
          placeholder="搜索用户名或姓名"
          clearable
          size="small"
        >
          <template #left-icon>
            <van-icon name="search" />
          </template>
          <template #right-icon>
            <van-icon 
              name="filter-o" 
              @click="showSearchFilter = true"
              :class="{ 'filter-active': hasActiveFilters }"
            />
          </template>
        </van-field>
      </div>

      <!-- 用户列表 -->
      <div class="user-list">
        <div v-if="filteredUserList.length === 0" class="empty-state">
          <van-empty description="暂无用户数据" />
        </div>
        <van-card
          v-for="user in filteredUserList"
          :key="user.id"
          class="user-card"
          @click="editUser(user)"
        >
          <template #title>
            <div class="user-card-header">
              <div class="name-status-container">
                <span class="username">{{ user.name }}</span>
                <van-tag :type="user.state === 0 ? 'success' : 'danger'" size="small">
                  {{ user.state === 0 ? '正常' : '禁用' }}
                </van-tag>
              </div>
              <van-tag :type="getRoleTagType(getRoleDesc(user.roleID))" size="small">
                {{ getRoleDesc(user.roleID) }}
              </van-tag>
            </div>
          </template>
          <template #desc>
            <div class="user-card-content">
              <div class="info-row">
                <span class="label">用户名：</span>
                <span class="value">{{ user.userName }}</span>
              </div>
              <div class="info-row">
                <span class="label">联系电话：</span>
                <span class="value">{{ user.phone }}</span>
              </div>
              <div class="info-row">
                <span class="label">所属码头：</span>
                <span class="value">{{ getPortDesc(user.portCode) }}</span>
              </div>
              <div class="info-row">
                <span class="label">创建时间：</span>
                <span class="value">{{ formatDate(user.createTime) }}</span>
              </div>
            </div>
          </template>
        </van-card>
      </div>
    </div>

    <!-- 修改密码弹窗 -->
    <van-popup
      v-model="showChangePassword"
      position="bottom"
      :style="{ height: '60%' }"
      round
    >
      <div class="popup-header">
        <h3>修改密码</h3>
        <van-icon name="cross" @click="showChangePassword = false" />
      </div>
      <van-form @submit="onChangePassword" class="password-form">
        <van-field
          v-model="passwordForm.oldPassword"
          type="password"
          name="oldPassword"
          label="原密码"
          placeholder="请输入原密码"
          :rules="[{ required: true, message: '请输入原密码' }]"
        />
        <van-field
          v-model="passwordForm.newPassword"
          type="password"
          name="newPassword"
          label="新密码"
          placeholder="请输入新密码"
          :rules="[{ required: true, message: '请输入新密码' }]"
        />
        <van-field
          v-model="passwordForm.confirmPassword"
          type="password"
          name="confirmPassword"
          label="确认密码"
          placeholder="请再次输入新密码"
          :rules="[{ required: true, message: '请再次输入新密码' }]"
        />
        <div class="form-actions">
          <van-button
            round
            block
            type="primary"
            native-type="submit"
            :loading="passwordLoading"
          >
            确认修改
          </van-button>
        </div>
      </van-form>
    </van-popup>

    <!-- 创建用户弹窗 -->
    <van-popup
      v-model="showCreateUser"
      position="bottom"
      :style="{ height: '70%' }"
      round
    >
      <div class="popup-header">
        <h3>创建用户</h3>
        <van-icon name="cross" @click="showCreateUser = false" />
      </div>
      <van-form @submit="onCreateUser" class="create-user-form">
        <van-field
          v-model="createUserForm.username"
          name="username"
          label="用户名"
          placeholder="请输入用户名"
          :rules="[{ required: true, message: '请输入用户名' }]"
        />
        <van-field
          v-model="createUserForm.name"
          name="name"
          label="姓名"
          placeholder="请输入姓名"
          :rules="[{ required: true, message: '请输入姓名' }]"
        />
        <van-field
          v-model="createUserForm.phone"
          name="phone"
          label="联系电话"
          placeholder="请输入联系电话"
          :rules="[{ required: true, message: '请输入联系电话' }]"
        />
        <van-field
          name="port"
          label="所属码头"
          placeholder="请选择所属码头"
          readonly
          is-link
          v-model="createUserForm.port"
          @click="onPortClick"
          :rules="[{ required: true, message: '请选择所属码头' }]"
        />
        <van-field
          name="role"
          label="角色"
          placeholder="请选择角色"
          readonly
          is-link
          v-model="createUserForm.role"
          @click="showRolePicker = true"
          :rules="[{ required: true, message: '请选择角色' }]"
        />
        <div class="form-actions">
          <van-button
            round
            block
            type="primary"
            native-type="submit"
            :loading="createUserLoading"
          >
            创建用户
          </van-button>
        </div>
      </van-form>
    </van-popup>

    <!-- 角色选择器 -->
    <van-popup v-model="showRolePicker" position="bottom">
      <van-picker
        :columns="roleColumns"
        show-toolbar
        title="选择角色"
        @confirm="onRoleConfirm"
        @cancel="showRolePicker = false"
      />
    </van-popup>

    <!-- 码头选择器 -->
    <van-popup v-model="showPortPicker" position="bottom">
      <van-picker
        :columns="portColumns"
        show-toolbar
        title="选择码头"
        @confirm="onPortConfirm"
        @cancel="showPortPicker = false"
      />
    </van-popup>

    <!-- 编辑用户弹窗 -->
    <van-popup
      v-model="showEditUser"
      position="bottom"
      :style="{ height: '70%' }"
      round
    >
      <div class="popup-header">
        <h3>编辑用户</h3>
        <van-icon name="cross" @click="showEditUser = false" />
      </div>
      <van-form  class="edit-user-form">
        <van-field
          v-model="editUserForm.userName"
          name="userName"
          label="用户名"
          readonly
        />
        <van-field
          v-model="editUserForm.name"
          name="name"
          label="姓名"
          placeholder="请输入姓名"
          :rules="[{ required: true, message: '请输入姓名' }]"
        />
        <van-field
          v-model="editUserForm.phone"
          name="phone"
          label="联系电话"
          placeholder="请输入联系电话"
          :rules="[{ required: true, message: '请输入联系电话' }]"
        />
        <van-field
          name="port"
          label="所属码头"
          placeholder="请选择所属码头"
          readonly
          is-link
          v-model="editUserForm.port"
          @click="onEditPortClick"
          :rules="[{ required: true, message: '请选择所属码头' }]"
        />
        <van-field
          name="role"
          label="角色"
          placeholder="请选择角色"
          readonly
          is-link
          v-model="editUserForm.role"
          @click="showEditRolePicker = true"
          :rules="[{ required: true, message: '请选择角色' }]"
        />
        <van-field
          name="state"
          label="状态"
          placeholder="请选择状态"
          readonly
          is-link
          v-model="editUserForm.state"
          @click="showStatePicker = true"
          :rules="[{ required: true, message: '请选择状态' }]"
        />
        <div class="form-actions">
          <van-button
            round
            block
            type="primary"

            @click="onEditUser"
            :loading="editUserLoading"
          >
            保存修改
          </van-button>
        </div>
        
        <!-- 危险操作区域 -->
        <div class="danger-actions">
          <van-button
            round
            size="small"
            type="warning"
            @click="confirmResetPassword"
          >
            重置密码
          </van-button>
          <van-button
            round
            size="small"
            type="danger"
            @click="confirmDeleteUser"
          >
            删除用户
          </van-button>
        </div>
      </van-form>
    </van-popup>

    <!-- 编辑角色选择器 -->
    <van-popup v-model="showEditRolePicker" position="bottom">
      <van-picker
        :columns="roleColumns"
        show-toolbar
        title="选择角色"
        @confirm="onEditRoleConfirm"
        @cancel="showEditRolePicker = false"
      />
    </van-popup>

    <!-- 状态选择器 -->
    <van-popup v-model="showStatePicker" position="bottom">
      <van-picker
        :columns="stateColumns"
        show-toolbar
        title="选择状态"
        @confirm="onStateConfirm"
        @cancel="showStatePicker = false"
      />
    </van-popup>

    <!-- 查询角色选择器 -->
    <van-popup v-model="showSearchRolePicker" position="bottom">
      <van-picker
        :columns="roleColumns"
        show-toolbar
        title="选择角色"
        @confirm="onSearchRoleConfirm"
        @cancel="showSearchRolePicker = false"
      />
    </van-popup>

    <!-- 查询码头选择器 -->
    <van-popup v-model="showSearchPortPicker" position="bottom">
      <van-picker
        :columns="portColumns"
        show-toolbar
        title="选择码头"
        @confirm="onSearchPortConfirm"
        @cancel="showSearchPortPicker = false"
      />
    </van-popup>

    <!-- 查询状态选择器 -->
    <van-popup v-model="showSearchStatePicker" position="bottom">
      <van-picker
        :columns="stateColumns"
        show-toolbar
        title="选择状态"
        @confirm="onSearchStateConfirm"
        @cancel="showSearchStatePicker = false"
      />
    </van-popup>

    <!-- 筛选条件弹窗 -->
    <van-popup
      v-model="showSearchFilter"
      position="bottom"
      :style="{ height: '60%' }"
      round
    >
      <div class="popup-header">
        <h3>筛选条件</h3>
        <van-icon name="cross" @click="showSearchFilter = false" />
      </div>
      <div class="filter-form">
        <van-field
          v-model="searchForm.name"
          label="姓名"
          placeholder="请输入姓名"
          clearable
        />
        <van-field
          name="role"
          label="角色"
          placeholder="请选择角色"
          readonly
          is-link
          v-model="searchForm.role"
          @click="showSearchRolePicker = true"
        />
        <van-field
          name="port"
          label="码头"
          placeholder="请选择码头"
          readonly
          is-link
          v-model="searchForm.port"
          @click="showSearchPortPicker = true"
        />
        <van-field
          name="state"
          label="状态"
          placeholder="请选择状态"
          readonly
          is-link
          v-model="searchForm.state"
          @click="showSearchStatePicker = true"
        />
        <div class="filter-actions">
          <van-button
            round
            block
            type="primary"
            @click="applyFilter"
          >
            应用筛选
          </van-button>
          <van-button
            round
            block
            plain
            @click="resetFilter"
          >
            重置筛选
          </van-button>
        </div>
      </div>
    </van-popup>



    <!-- 自定义确认弹窗 -->
    <van-popup
      v-model="showCustomConfirm"
      :style="{ width: '85%', borderRadius: '16px' }"
      round
      :close-on-click-overlay="false"
    >
      <div class="custom-confirm-dialog">
        <div class="confirm-icon">
          <van-icon 
            :name="confirmConfig.icon" 
            :color="confirmConfig.iconColor" 
            size="48"
          />
        </div>
        <div class="confirm-title">
          {{ confirmConfig.title }}
        </div>
        <div class="confirm-message">
          {{ confirmConfig.message }}
        </div>
        <div class="confirm-actions">
          <van-button
            round
            size="small"
            plain
            @click="cancelConfirm"
            :disabled="confirmConfig.loading"
          >
            取消
          </van-button>
          <van-button
            round
            size="small"
            :type="confirmConfig.buttonType"
            :loading="confirmConfig.loading"
            @click="executeConfirm"
          >
            {{ confirmConfig.buttonText }}
          </van-button>
        </div>
      </div>
    </van-popup>
    </div>
  </van-pull-refresh>
</template>

<script>
import md5 from 'blueimp-md5'
import globalDic, { fetchMultiDic } from '@/utils/dic'
export default {
  name: 'UserManagement',
  data() {
    return {
      refreshing: false,
      userList: [], // 从接口获取用户列表
      showChangePassword: false,
      showCreateUser: false,
      showRolePicker: false,
      showPortPicker: false,
      showEditUser: false,
      showEditRolePicker: false,
      showStatePicker: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      createUserForm: {
        username: '',
        name: '',
        phone: '',
        port: '',
        role: ''
      },
      editUserForm: {
        id: '',
        userName: '',
        name: '',
        phone: '',
        port: '',
        role: '',
        state: ''
      },
      passwordLoading: false,
      createUserLoading: false,
      editUserLoading: false,
      resetPasswordLoading: false,
      deleteUserLoading: false,
      resetPasswordRequested: false,
      deleteUserRequested: false,
      showCustomConfirm: false,
      confirmConfig: {
        title: '',
        message: '',
        icon: '',
        iconColor: '',
        buttonType: 'primary',
        buttonText: '确认',
        loading: false,
        action: null
      },

      // 查询相关
      showSearchFilter: false,
      showSearchRolePicker: false,
      showSearchPortPicker: false,
      showSearchStatePicker: false,
      searchForm: {
        userName: '',
        name: '',
        role: '',
        port: '',
        state: ''
      },
      searchLoading: false,
      allUserList: [], // 存储所有用户数据
    };
  },
  
  computed: {
    currentUser() {
      return JSON.parse(localStorage.getItem('userInfo') || '{}');
    },
    roleDic() {
      return globalDic.D_Role || [];
    },
    currentUserRoleDesc() {
      const found = this.roleDic.find(item => item.code === String(this.currentUser.roleID));
      return found ? found.description : this.currentUser.roleID;
    },
    portDic() {
      return globalDic.D_Port || [];
    },
    roleColumns() {
      // 只能选择 roleId 大于当前用户 roleId 的角色
      const currentUserRoleId = this.currentUser.roleID;
      return this.roleDic
        .filter(item => parseInt(item.code) > parseInt(currentUserRoleId))
        .map(item => item.description);
    },
    portColumns() {
      return this.portDic.map(item => item.description);
    },
    isSuperAdmin() {
      return this.currentUser.roleID === 0;
    },
    currentUserPortDesc() {
      const found = this.portDic.find(item => item.code === this.currentUser.portCode);
      return found ? found.description : this.currentUser.portCode;
    },
    stateColumns() {
      return ['正常', '禁用'];
    },
    hasActiveFilters() {
      return this.searchForm.role || this.searchForm.port || this.searchForm.state;
    },
    filteredUserList() {
      let filtered = this.allUserList;
      
      // 搜索框筛选（同时匹配用户名和姓名）
      if (this.searchForm.userName) {
        const searchTerm = this.searchForm.userName.toLowerCase();
        filtered = filtered.filter(user => 
          (user.userName && user.userName.toLowerCase().includes(searchTerm)) ||
          (user.name && user.name.toLowerCase().includes(searchTerm))
        );
      }
      
      // 姓名筛选（弹窗中的姓名字段）
      if (this.searchForm.name) {
        filtered = filtered.filter(user => 
          user.name && user.name.toLowerCase().includes(this.searchForm.name.toLowerCase())
        );
      }
      
      // 角色筛选
      if (this.searchForm.role) {
        const roleID = this.getRoleIDByName(this.searchForm.role);
        filtered = filtered.filter(user => user.roleID == roleID);
      }
      
      // 码头筛选
      if (this.searchForm.port) {
        const portCode = this.getPortCodeByName(this.searchForm.port);
        filtered = filtered.filter(user => user.portCode == portCode);
      }
      
      // 状态筛选
      if (this.searchForm.state) {
        const stateValue = this.searchForm.state === '正常' ? 0 : 1;
        filtered = filtered.filter(user => Number(user.state) === stateValue);
      }
      
      return filtered;
    }
  },
  
  async mounted() {
    if (!globalDic.D_Port || globalDic.D_Port.length === 0) {
      await fetchMultiDic(['D_Role', 'D_Port', 'D_AlarmType']);
    }
    console.log('currentUser:', this.currentUser);
    console.log('roleColumns:', this.portColumns);
    // 非超级管理员自动设置当前用户的码头
    if (!this.isSuperAdmin) {
      this.createUserForm.port = this.currentUserPortDesc;
    }
    // 查询用户列表
    this.loadUserList();
  },
  
  methods: {
    // 加载用户列表
    loadUserList() {
      this.$axios = this.$axios || require('@/utils/request').default;
      return this.$axios.post('/api/User/Query', {
        userName: '',
        portCode: '',
        state: -99,
        roleID: -99
      }).then(res => {
        if (res && res.data && res.data.success) {
          // 过滤用户列表，只显示角色ID比当前用户大的用户         
          const allUsers = res.data.data || [];
          this.allUserList = allUsers.filter(user => {
            const userRoleID = parseInt(user.roleID);
            const currentRoleID = parseInt(this.currentUser.roleID);
            return userRoleID > currentRoleID;
          });
        } else {
          this.$toast.fail('获取用户列表失败');
        }
      }).catch(() => {
        this.$toast.fail('获取用户列表失败');
      });
    },
    async onRefresh() {
      try {
        await this.loadUserList();
      } finally {
        this.refreshing = false;
      }
    },

    // 查询用户
    searchUsers() {
      // 本地筛选，不需要调用API
      this.searchLoading = false;
    },
    
    // 根据角色ID获取角色描述
    getRoleDesc(roleID) {
      const found = this.roleDic.find(item => item.code === String(roleID));
      return found ? found.description : roleID;
    },
    
    // 根据码头代码获取码头描述
    getPortDesc(portCode) {
      const found = this.portDic.find(item => item.code === portCode);
      return found ? found.description : portCode;
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN');
    },
    
    getRoleTagType(role) {
      const typeMap = {
        '系统管理员': 'primary',
        '操作员': 'success',
        '查看员': 'warning'
      };
      return typeMap[role] || 'default';
    },
    
    onChangePassword(values) {
      if (!values.oldPassword) {
        this.$toast.fail('请输入原密码');
        return;
      }
      // 如果currentUser有password字段，前端可校验原密码
      if (this.currentUser.password && md5(values.oldPassword) !== this.currentUser.password) {
        this.$toast.fail('原密码输入错误');
        return;
      }
      if (values.newPassword !== values.confirmPassword) {
        this.$toast.fail('两次输入的密码不一致');
        return;
      }
      this.passwordLoading = true;
      console.log('onChangePassword');
      // 调用后端接口修改密码
      this.$axios.post('/api/User/Modify', {
        id: this.currentUser.userId || this.currentUser.id,
        password: md5(values.newPassword),
      }).then(res => {
        if (res && res.data && res.data.success) {
          this.$toast.success('密码修改成功,请重新登录');
          //this.showChangePassword = false;
          this.passwordForm = {
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
          };
          // 清除token等信息
            localStorage.removeItem('token');
            localStorage.removeItem('userInfo');
            localStorage.removeItem('username');
            this.showChangePassword = false;
            this.$router.replace('/login');
        } else {
          this.$toast.fail(res.data.message || '密码修改失败');
        }
      }).catch(() => {
        this.$toast.fail('密码修改失败');
      }).finally(() => {
        this.passwordLoading = false;
      });
    },
    
    onCreateUser(values) {
      this.createUserLoading = true;
      // 根据选择的角色描述找到对应的 code
      const selectedRole = this.roleDic.find(item => item.description === values.role);
      const selectedPort = this.portDic.find(item => item.description === values.port);
      
      if (!selectedRole || !selectedPort) {
        this.$toast.fail('角色或码头选择有误');
        this.createUserLoading = false;
        return;
      }
      
      // 调用后端接口创建用户
      this.$axios = this.$axios || require('@/utils/request').default;
      this.$axios.post('/api/User/Add', {
        userName: values.username,
        password: md5('111111'), // 默认密码111111的md5值
        name: values.name,
        phone: values.phone,
        portCode: selectedPort.code, // 选择所属码头对应的code
        state: 0, // 默认0
        roleID: parseInt(selectedRole.code), // 选择角色对应的code
        createID: this.currentUser.userId || this.currentUser.id // 当前登录用户的id
      }).then(res => {
        if (res && res.data && res.data.success) {
          this.$toast.success('用户创建成功');
          this.showCreateUser = false;
          this.createUserForm = {
            username: '',
            name: '',
            phone: '',
            port: '',
            role: ''
          };
          // 刷新用户列表
          this.loadUserList();
        } else {
          this.$toast.fail(res.data.message || '用户创建失败');
        }
      }).catch(() => {
        this.$toast.fail('用户创建失败');
      }).finally(() => {
        this.createUserLoading = false;
      });
    },
    
    onRoleConfirm(value) {
      console.log('选择角色:', value);
      this.createUserForm.role = value;
      this.showRolePicker = false;
    },
    
    onPortClick() {
      // 只有超级管理员才能选择码头
      if (this.isSuperAdmin) {
        this.showPortPicker = true;
      } else {
        this.$toast('只有超级管理员才能选择码头');
      }
    },
    
    onPortConfirm(value) {
      console.log('选择码头:', value);
      this.createUserForm.port = value;
      this.showPortPicker = false;
    },
    
    onEditPortClick() {
      // 只有超级管理员才能选择码头
      if (this.isSuperAdmin) {
        this.showPortPicker = true;
      } else {
        this.$toast('只有超级管理员才能选择码头');
      }
    },
    
    onEditRoleConfirm(value) {
      console.log('编辑选择角色:', value);
      this.editUserForm.role = value;
      this.showEditRolePicker = false;
    },
    
    onStateConfirm(value) {
      console.log('选择状态:', value);
      this.editUserForm.state = value;
      this.showStatePicker = false;
    },
    
    editUser(user) {
      // 填充编辑表单
      this.editUserForm = {
        id: user.id,
        userName: user.userName,
        name: user.name,
        phone: user.phone,
        port: this.getPortDesc(user.portCode),
        role: this.getRoleDesc(user.roleID),
        state: user.state === 0 ? '正常' : '禁用'
      };
      this.showEditUser = true;
    },
    

    
    onEditUser() {
     
      this.editUserLoading = true;
      
      // 根据选择的角色描述找到对应的 code
      const selectedRole = this.roleDic.find(item => item.description === this.editUserForm.role);
      const selectedPort = this.portDic.find(item => item.description === this.editUserForm.port);
      
      if (!selectedRole || !selectedPort) {
        this.$toast.fail('角色或码头选择有误');
        this.editUserLoading = false;
        return;
      }
      console.log('onEditUser password');
      // 调用后端接口修改用户
      this.$axios = this.$axios || require('@/utils/request').default;
      this.$axios.post('/api/User/Modify', {
        id: this.editUserForm.id,
        userName: this.editUserForm.userName,
        name: this.editUserForm.name,
        phone: this.editUserForm.phone,
        portCode: selectedPort.code,
        state: this.editUserForm.state === '正常' ? 0 : 1,
        roleID: parseInt(selectedRole.code)
      }).then(res => {
        if (res && res.data && res.data.success) {
          this.$toast.success('用户修改成功');
          this.showEditUser = false;
          // 刷新用户列表
          this.loadUserList();
        } else {
          this.$toast.fail(res.data.message || '用户修改失败');
        }
      }).catch(() => {
        this.$toast.fail('用户修改失败');
      }).finally(() => {
        this.editUserLoading = false;
      });
    },
    
    // 确认重置密码
    confirmResetPassword() {
      this.confirmConfig = {
        title: '重置密码',
        message: '确定要将该用户的密码重置为初始密码(111111)吗？',
        icon: 'warning-o',
        iconColor: '#ff976a',
        buttonType: 'warning',
        buttonText: '重置密码',
        loading: false,
        action: 'resetPassword'
      };
      this.showCustomConfirm = true;
    },
    
    // 重置密码
    resetPassword() {
      if (this.resetPasswordRequested) {
        console.log('重置密码请求已发起，忽略重复调用');
        return;
      }
      
      this.resetPasswordRequested = true;
      this.resetPasswordLoading = true;
      this.confirmConfig.loading = true;
      
      const axiosInstance = this.$axios || require('@/utils/request').default;
      console.log('发起重置密码请求');
      
      axiosInstance.post('/api/User/Modify', {
        id: this.editUserForm.id,
        password: md5('111111') // 重置为初始密码111111的md5值
      }).then(res => {
        console.log('重置密码请求成功');
        if (res && res.data && res.data.success) {
          this.$toast.success('密码重置成功');
          this.showCustomConfirm = false;
        } else {
          this.$toast.fail(res.data.message || '密码重置失败');
        }
      }).catch((error) => {
        console.log('重置密码请求失败:', error);
        this.$toast.fail('密码重置失败');
      }).finally(() => {
        this.resetPasswordLoading = false;
        this.resetPasswordRequested = false;
        this.confirmConfig.loading = false;
      });
    },  

    
    // 确认删除用户
    confirmDeleteUser() {
      this.confirmConfig = {
        title: '删除用户',
        message: '确定要删除该用户吗？此操作不可恢复！',
        icon: 'delete-o',
        iconColor: '#ee0a24',
        buttonType: 'danger',
        buttonText: '删除用户',
        loading: false,
        action: 'deleteUser'
      };
      this.showCustomConfirm = true;
    },
    
    // 取消确认
    cancelConfirm() {
      this.showCustomConfirm = false;
    },
    
    // 执行确认操作
    executeConfirm() {
      if (this.confirmConfig.action === 'resetPassword') {
        this.resetPassword();
      } else if (this.confirmConfig.action === 'deleteUser') {
        this.deleteUser();
      }
    },
    
    // 删除用户
    deleteUser() {
      if (this.deleteUserRequested) {
        console.log('删除用户请求已发起，忽略重复调用');
        return;
      }
      
      this.deleteUserRequested = true;
      this.deleteUserLoading = true;
      this.confirmConfig.loading = true;
      
      const axiosInstance = this.$axios || require('@/utils/request').default;
      console.log('发起删除用户请求');
      
      axiosInstance.post('/api/User/Delete', {
        id: this.editUserForm.id
      }).then(res => {
        console.log('删除用户请求成功');
        if (res && res.data && res.data.success) {
          this.$toast.success('用户删除成功');
          this.showCustomConfirm = false;
          // 关闭编辑弹窗
          this.showEditUser = false;
          // 刷新用户列表
          this.loadUserList();
        } else {
          this.$toast.fail(res.data.message || '用户删除失败');
        }
      }).catch((error) => {
        console.log('删除用户请求失败:', error);
        this.$toast.fail('用户删除失败');
      }).finally(() => {
        this.deleteUserLoading = false;
        this.deleteUserRequested = false;
        this.confirmConfig.loading = false;
      });
    },

    // 重置查询条件
    resetSearch() {
      this.searchForm = {
        userName: '',
        name: '',
        role: '',
        port: '',
        state: ''
      };
    },

    // 根据角色名称获取角色ID
    getRoleIDByName(roleName) {
      const role = this.roleDic.find(item => item.description === roleName);
      return role ? role.code : -99;
    },

    // 根据码头名称获取码头代码
    getPortCodeByName(portName) {
      const port = this.portDic.find(item => item.description === portName);
      return port ? port.code : '';
    },

    // 查询角色选择器确认
    onSearchRoleConfirm(value) {
      this.searchForm.role = value;
      this.showSearchRolePicker = false;
    },

    // 查询码头选择器确认
    onSearchPortConfirm(value) {
      this.searchForm.port = value;
      this.showSearchPortPicker = false;
    },

    // 查询状态选择器确认
    onSearchStateConfirm(value) {
      this.searchForm.state = value;
      this.showSearchStatePicker = false;
    },

    // 应用筛选
    applyFilter() {
      this.showSearchFilter = false;
    },

    // 重置筛选
    resetFilter() {
      this.searchForm = {
        userName: this.searchForm.userName, // 保留搜索框的内容
        name: '',
        role: '',
        port: '',
        state: ''
      };
    },

  }
};
</script>

<style scoped>
.user-management {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 100%;
}

.user-info-card {
  margin-bottom: 0;
  border-radius: 24px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(215, 229, 251, 0.96);
  box-shadow: var(--shadow-card);
}

.user-info-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.title-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-info-title {
  font-weight: bold;
  font-size: 16px;
}

.user-info-content {
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item, .info-row {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.label {
  color: var(--text-secondary);
  font-size: 14px;
  width: 80px;
  flex-shrink: 0;
}

.value {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  word-break: break-all;
}

.change-password-btn {
  border-radius: 20px;
}

.management-section {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  padding: 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(215, 229, 251, 0.96);
  border-radius: 24px;
  box-shadow: var(--shadow-card);
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.search-bar {
  margin-bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  padding: 10px;
  border: 1px solid rgba(215, 229, 251, 0.96);
  border-radius: 24px;
  box-shadow: var(--shadow-card);
}

.filter-active {
  color: #1989fa !important;
}

.filter-form {
  padding: 16px;
}

.filter-actions {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.user-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.user-card {
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(215, 229, 251, 0.96);
  box-shadow: var(--shadow-card);
}

.user-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.name-status-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.username {
  font-weight: bold;
  font-size: 16px;
}

.user-card-content {
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-actions {
  display: flex;
  gap: 8px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #ebedf0;
}

.popup-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: bold;
}

.password-form, .create-user-form {
  padding: 16px;
}

.form-actions {
  margin-top: 24px;
}

:deep(.van-card__content) {
  padding: 14px 16px;
}

.danger-actions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ebedf0;
  display: flex;
  gap: 12px;
  justify-content: center;
}

.danger-actions .van-button {
  flex: 1;
  max-width: 120px;
}

/* 自定义确认弹窗样式 */
.custom-confirm-dialog {
  padding: 32px 24px 24px;
  text-align: center;
  background: white;
  border-radius: 16px;
}

.confirm-icon {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.confirm-title {
  font-size: 18px;
  font-weight: bold;
  color: #323233;
  margin-bottom: 12px;
}

.confirm-message {
  font-size: 14px;
  color: #646566;
  line-height: 1.5;
  margin-bottom: 24px;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.confirm-actions .van-button {
  min-width: 80px;
  height: 36px;
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .user-management {
    background-color: #1a1a1a;
  }
  
  .user-info-card,
  .section-header,
  .search-bar {
    background: #2a2a2a;
    border-color: #444;
  }
  
  .section-header h3 {
    color: white;
  }
  
  .value {
    color: #ccc;
  }
  
  .user-card {
    background: #333;
    border-color: #444;
  }
  
  .popup-header {
    background: #2a2a2a;
    border-bottom-color: #444;
  }
  
  .popup-header h3 {
    color: white;
  }
}

@media (max-width: 767px) {
  .user-management {
    gap: 14px;
  }

  .section-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-bar {
    padding: 8px 12px;
  }

  .user-list {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}
</style>
